import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  Link,
  Paper,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import conObj from '../../utils/connection-assistant';
import Image from '../../components/Image';
import { useDispatch, useSelector } from '../../redux/store';
import { HEADER } from '../../config';
import useLocales from '../../hooks/useLocales';
import { closeLoadingScreen, openLoadingScreen } from '../../redux/slices/settings';
import Scrollbar from '../../components/Scrollbar';
import GameContainer from './GameContainer';
import { calculateFontSize } from '../../utils/formatString';
import ProviderByCateg from './ProviderByCateg';
import { openLoginSignup } from '../../redux/slices/authPages';
import { fNumber } from '../../utils/formatNumber';

const SectionHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(273.4deg, rgba(3, 0, 165, 0) 12.81%, #4200FF 96.52%)',
  borderRadius: '5px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '2px 5px',
  marginBottom: '10px',
}));

const SectionIconWrapper = styled(Box)(({ theme }) => ({
  width: '10%',
  minWidth: '23px',
  [theme.breakpoints.up('sm')]: {
    width: '12%',
  },
  [theme.breakpoints.up('md')]: {
    width: '15%',
  },
  [theme.breakpoints.up('lg')]: {
    width: '16%',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: '14px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '15px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '17px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '19px',
  },
}));

const TableCellNoBg = styled(TableCell)(({ theme }) => ({
  background: 'none',
  color: '#fff',
  fontSize: '10px',
  textAlign: 'start',
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '14px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '17px',
  },
}));

const HomeBody = ({ popupAuth = '0' }) => {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { gameCategsAndCompanies, fakePlayerStats } = useSelector((x) => x.lookup);

  const { translate } = useLocales();

  const [isLoadingPopGames, setIsLoadingPopGames] = useState(false);

  const location = useLocation();

  const [popularGames, setPopularGames] = useState({
    data: [],
    page: 1,
  });

  const gameProviders = useMemo(() => {
    const providers = [];

    gameCategsAndCompanies.forEach((item) =>
      providers.push(...item.companies.map((x) => ({ ...x, categSlug: item.slug })))
    );

    return providers;
  }, [gameCategsAndCompanies]);

  const fake10 = useMemo(() => {
    if (fakePlayerStats?.length) {
      return fakePlayerStats.slice(0, 10);
    }

    return [];
  }, [fakePlayerStats]);

  const getPopularGames = () => {
    setIsLoadingPopGames(true);
    dispatch(openLoadingScreen());
    conObj
      .post(`/games/items.json?sort=popularity&limit=${window.innerWidth < 500 ? 12 : 18}&page=${popularGames?.page}`)
      .then((res) => {
        if (res.status === 200) {
          setPopularGames((prevState) => ({
            data: [...prevState?.data, ...res.data?.data?.data],
            page: prevState?.page + 1,
          }));
          setIsLoadingPopGames(false);
        }
      })
      .catch((err) => {
        setIsLoadingPopGames(false);
        console.log(`Error: ${err.message}`);
      })
      .finally(() => dispatch(closeLoadingScreen()));
  };

  useEffect(() => {
    getPopularGames();
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('accessToken')) {
      if (popupAuth === '1') dispatch(openLoginSignup({ open: true, isLogin: true }));
      // if (popupAuth === '2') dispatch(openLoginSignup({ open: true, isLogin: false }));
    }
  }, [popupAuth]);

  if (location.state?.categ && location.state?.categ !== 'home') return <ProviderByCateg />;

  return (
    <>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', position: 'relative' }}>
        <Image
          src={require('../../assets/home/jackpot-ctr.webp')}
          sx={{ width: { xs: '70%', sm: '70%', md: '60%' }, m: 2 }}
        />
        <Image
          src={require('../../assets/home/jackpot-stats.webp')}
          sx={{ position: 'absolute', top: { xs: '16%', sm: '16%', md: '13%' }, width: '35%' }}
        />
      </Box>

      {popularGames?.data?.length > 0 && (
        <Box id={'Popular'} sx={{ scrollMarginTop: HEADER.MOBILE_HEIGHT, px: '0.5rem', pb: '0.5rem' }}>
          <SectionHeader>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <SectionIconWrapper>
                <Image src={require('../../assets/menu/nav-slot-white.png')} alt="All Games" sx={{ width: '100%' }} />
              </SectionIconWrapper>
              <SectionTitle>{translate('popular_games')}</SectionTitle>
            </Box>
            <Link
              sx={{ color: '#D9D0F1', fontSize: '0.8rem', whiteSpace: 'nowrap', cursor: 'pointer' }}
              onClick={() => navigate('/games/popular')}
            >
              {translate('show_all')}
            </Link>
          </SectionHeader>
          <Scrollbar>
            <Stack direction="row" spacing={1}>
              {popularGames?.data?.map((g, gIndex) => (
                <Box key={gIndex} sx={{ minHeight: '120px', minWidth: '120px' }}>
                  <GameContainer item={g} titleSx={{ fontSize: calculateFontSize(g?.name, 14, 18) }} hasShadow />
                </Box>
              ))}
            </Stack>
          </Scrollbar>
        </Box>
      )}

      {gameProviders?.length > 0 && (
        <Box sx={{ scrollMarginTop: HEADER.MOBILE_HEIGHT, p: '0.5rem' }}>
          <SectionHeader>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <SectionIconWrapper>
                <Image src={require('../../assets/home/game-provider.png')} sx={{ width: '100%' }} />
              </SectionIconWrapper>
              <SectionTitle>{translate('game_provider')}</SectionTitle>
            </Box>
          </SectionHeader>

          <Grid container spacing={{ md: 2, sm: 2, xs: 1 }}>
            {gameProviders.map((g, gIndex) => (
              <Grid key={gIndex} item xl={3} lg={3} md={4} sm={6} xs={6}>
                <GameContainer
                  item={g}
                  companySlug={g?.categSlug}
                  // containerSx={{
                  //   background:
                  //     'linear-gradient(0deg, rgba(1,161,1,1) 0%, rgba(1,161,1,1) 90%, rgba(178,255,1,1) 100%)',
                  // }}
                  // imgWrapperSx={{ width: '100px', minHeight: '140px' }}
                  titleSx={{ fontSize: calculateFontSize(g?.name, 14, 18) }}
                  hasShadow
                  isProvider={true}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Box sx={{ scrollMarginTop: HEADER.MOBILE_HEIGHT, p: '0.5rem' }}>
        <SectionHeader sx={{ mb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <SectionIconWrapper>
              <Image src={require('../../assets/home/leaderboard.png')} sx={{ width: '100%' }} />
            </SectionIconWrapper>
            <SectionTitle>{translate('leaderboard')}</SectionTitle>
          </Box>
        </SectionHeader>

        <Box sx={{ width: '100%' }}>
          <TableContainer>
            <Table sx={{ width: '100%' }} size="small">
              <TableHead>
                <TableRow>
                  <TableCellNoBg sx={{ py: 0 }}>{translate('username')}</TableCellNoBg>

                  <TableCellNoBg sx={{ minWidth: '90px', textAlign: 'center', py: 0 }}>
                    {translate('game_name')}
                  </TableCellNoBg>

                  <TableCellNoBg sx={{ py: 0 }}>{translate('winnings')}</TableCellNoBg>
                </TableRow>
              </TableHead>

              <TableBody>
                {fake10.map((x, idx) => (
                  <TableRow key={idx} sx={{ background: (idx + 1) % 2 === 0 ? '#0A0095' : '#006BD2' }}>
                    <TableCellNoBg>{x.username}</TableCellNoBg>
                    <TableCellNoBg sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Box sx={{ width: { xs: '30%', sm: '30%', md: '20%' } }}>
                        <Image src={x.game_photo_url} sx={{ width: '100%' }} />
                      </Box>
                    </TableCellNoBg>
                    <TableCellNoBg>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{x.win}</span>
                        <Box sx={{ width: { xs: '30%', sm: '30%', md: '20%' } }}>
                          <Image src={require('../../assets/home/leaderboard-play.png')} sx={{ width: '100%' }} />
                        </Box>
                      </Box>
                    </TableCellNoBg>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </>
  );
};

HomeBody.propTypes = {};

export default HomeBody;
